/**
 * The profile's option vocabularies, from the config store.
 *
 * NO hardcoded fallback vocabulary: by the time these getters run, config is a
 * real backend response — a config FETCH failure is a visible error screen,
 * not a silent stand-in (api/endpoints/config.ts). A field missing from a REAL
 * config, or carrying no options, is a legitimate config fork: return [] and
 * render free text for it, never invent a vocabulary that can drift from what
 * the deployment actually configured.
 *
 * It used to read `onboarding.questions`, which carried the scripted
 * interview's prompt keys and required flags beside the vocabulary. The
 * interview is the onboarding module's content now; the vocabulary is what a
 * nurse editing her profile a year later still needs, so it moved to
 * `profile.fields` and left the script behind.
 */

import { useMemo } from 'react';

import { useConfigStore } from '../stores/config';
import type { ProfileFieldOptions, ProfileFieldOption } from '../api/types';

/** Resolved years option with i18n key and numeric value */
export interface YearsOption {
  labelKey: string;
  value: number;
}

interface ProfileFieldVocabularies {
  getYearsOptions: () => YearsOption[];
  /** The declared vocabulary for one profile field; `[]` when it has none. */
  optionsFor: (field: string) => string[];
  /** Whether the named onboarding question may still be edited by the user
   *  outside the interview (e.g. in the profile editor). A question marked
   *  `editable: false` in config is server-derived / auto-inserted — a field
   *  the config doesn't even mention (or config not yet loaded) defaults to
   *  editable, since "unknown" must never read as "locked". */
  isFieldEditable: (field: string) => boolean;
}

/** Type guard to check if option is a structured object (not a plain string) */
function isOptionObject(opt: string | ProfileFieldOption): opt is ProfileFieldOption {
  return typeof opt === 'object' && opt !== null;
}

/** Extract label key from option (handles both string and object formats) */
function extractLabelKey(opt: string | ProfileFieldOption): string {
  if (isOptionObject(opt)) {
    return opt.label_key ?? opt.label ?? String(opt.value);
  }
  return opt;
}

/** The profile's option vocabularies, read from the config store.
 *
 * The whole returned object is memoised on `config`, because ProfileEditForm
 * puts it in the dependency array of four useMemo calls. Returning a fresh
 * object each render would re-run all four on every render.
 */
export function useProfileFieldOptions(): ProfileFieldVocabularies {
  const { config } = useConfigStore();

  return useMemo(() => {
    const fieldFor = (field: string): ProfileFieldOptions | undefined =>
      config?.profile.fields.find((f) => f.field === field);

    const getYearsOptions = (): YearsOption[] => {
      const question = fieldFor('years_experience');
      if (question?.options && question.options.length > 0 && isOptionObject(question.options[0])) {
        return question.options
          .filter(isOptionObject)
          .map((opt) => ({
            labelKey: `onboarding.years.${opt.label_key ?? opt.label}`,
            value: typeof opt.value === 'number' ? opt.value : (typeof opt.value === 'string' ? parseInt(opt.value, 10) : Number(opt.value)),
          }));
      }
      return [];
    };

    // One getter over the list, not one per field. The three it replaced
    // differed by a string literal, so a fourth field with a vocabulary meant a
    // fourth copy — and a copy someone forgot to add reads as "this field has
    // no options", which is a real answer here and therefore invisible.
    const optionsFor = (field: string): string[] => {
      const declared = fieldFor(field);
      return declared?.options ? declared.options.map(extractLabelKey) : [];
    };

    const isFieldEditable = (field: string): boolean => fieldFor(field)?.editable ?? true;

    return { getYearsOptions, optionsFor, isFieldEditable };
  }, [config]);
}
